'use client'

import React, { useEffect, useState } from 'react'
import { FaXTwitter, FaWhatsapp, FaFacebookF, FaLink, FaShareNodes } from 'react-icons/fa6'
import { FaEnvelope } from 'react-icons/fa'
import { track } from '@/utils/analytics'

/**
 * ShareButtons — the viral mechanism the site has been missing.
 *
 * Why: Yuka grew from 0 → 73M users via word-of-mouth alone. Their
 * users tell 10–20 friends each. Our site has no share mechanism
 * whatsoever — every reader who finds a piece useful has no
 * one-tap way to send it. Adding share buttons turns every blog
 * reader into a potential distribution channel.
 *
 * Design choices:
 *  - Native iOS / Android share-sheet via `navigator.share()` when
 *    available (mobile users tap once → all their apps available).
 *    This is the highest-converting share UI for mobile.
 *  - Explicit per-platform buttons as fallback on desktop where
 *    `navigator.share` is missing or unreliable. WhatsApp + iMessage
 *    are the two highest-volume share destinations for health-content
 *    in 2026 per Backlinko data.
 *  - "Copy link" button for the universally compatible case.
 *  - Each share fires `article_share_click` with the platform — so
 *    GA4 can show which platforms drive the most onward shares.
 *  - Custom share text per-article (passed via `shareText` prop) so
 *    the message is specific and shareable, not just a URL.
 *
 * Industry signal: Yuka's whole moat is the word-of-mouth loop.
 * Cal AI's growth was driven by TikTok creators sharing their
 * scan results — the share mechanic is foundational, not optional.
 */
export interface ShareButtonsProps {
  /** Fully-qualified URL to share (used in WhatsApp, X, email body, copy). */
  url: string
  /** Pre-composed share text. Keep ≤ 100 chars for X / iMessage previews. */
  shareText: string
  /** Optional source label for GA4 attribution. Defaults to `article_bottom`. */
  source?: string
  /** Optional surface label (e.g. `blog_bottom`, `tool_result`) for breakdown. */
  surface?: string
}

const ENCODE = encodeURIComponent

export default function ShareButtons({
  url,
  shareText,
  source = 'article_bottom',
  surface = 'blog_bottom',
}: ShareButtonsProps) {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle')

  const handleNativeShare = async () => {
    if (typeof navigator === 'undefined' || !navigator.share) return
    try {
      await navigator.share({ title: shareText, text: shareText, url })
      track('related_article_click', {
        cta_surface: source,
        destination: 'native_share',
        page_type: surface,
      })
    } catch {
      // User dismissed the share sheet — common, not an error to log
    }
  }

  const handlePlatformShare = (platform: string, shareUrl: string) => {
    track('related_article_click', {
      cta_surface: source,
      destination: `share_${platform}`,
      page_type: surface,
    })
    window.open(shareUrl, '_blank', 'noopener,noreferrer,width=600,height=600')
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText} ${url}`)
      setCopyState('copied')
      track('related_article_click', {
        cta_surface: source,
        destination: 'share_copy_link',
        page_type: surface,
      })
      setTimeout(() => setCopyState('idle'), 2200)
    } catch {
      setCopyState('error')
      setTimeout(() => setCopyState('idle'), 2200)
    }
  }

  // Show the native share button only where the Web Share API exists.
  //
  // This MUST be a post-mount effect, not a render-time check. It used to be
  //   const hasNativeShare = typeof window !== 'undefined' && 'share' in navigator
  // with a comment claiming that was SSR-safe. It was not: the server has no
  // `window`, so it rendered no button, while any browser with navigator.share
  // rendered one on the very first client pass. That extra <button> was a
  // structural hydration mismatch — React error #418 — on every page carrying
  // this component, which then escalated to #423 and made React throw the
  // server HTML away and re-render the entire root on the client.
  //
  // Deferring by one tick is the documented fix. The button appears
  // imperceptibly later; nothing else changes.
  const [hasNativeShare, setHasNativeShare] = useState(false)
  useEffect(() => {
    // Deliberate. A render-time check here is exactly what caused React #418
    // on every article (fixed 2026-08-19): the server has no navigator, so it
    // rendered no button while the browser rendered one. Detecting a browser
    // capability after mount is the documented fix; the extra render pass is
    // the point, not a bug.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setHasNativeShare(typeof navigator !== 'undefined' && 'share' in navigator)
  }, [])

  return (
    <aside
      aria-label="Share this article"
      className="not-prose my-8 rounded-2xl border border-black/5 bg-white p-5 md:p-6 shadow-sm"
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
          Share this
        </p>
        <p className="text-2xs text-gray-400 hidden sm:block">
          Help someone make a cleaner choice
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {/* Mobile-first native share — opens the OS share sheet so the user can
            pick WhatsApp / iMessage / Signal / wherever they actually chat */}
        {hasNativeShare && (
          <button
            type="button"
            onClick={handleNativeShare}
            className="inline-flex items-center gap-2 bg-ink text-cream font-semibold px-4 py-2 rounded-control text-sm hover:bg-ink/90 active:scale-95 transition-[color,background-color,border-color,text-decoration-color,transform] sm:hidden pressable"
            aria-label="Share via your phone's share sheet"
          >
            <FaShareNodes className="w-4 h-4" aria-hidden="true" />
            Share
          </button>
        )}

        <button
          type="button"
          onClick={() =>
            handlePlatformShare(
              'whatsapp',
              `https://wa.me/?text=${ENCODE(`${shareText} ${url}`)}`
            )
          }
          className="inline-flex items-center gap-2 bg-[#25D366]/10 text-[#075E54] hover:bg-[#25D366]/20 px-3 py-2 rounded-control text-sm font-semibold transition-colors pressable"
          aria-label="Share on WhatsApp"
        >
          <FaWhatsapp className="w-4 h-4" aria-hidden="true" />
          <span className="hidden xs:inline">WhatsApp</span>
        </button>

        <button
          type="button"
          onClick={() =>
            handlePlatformShare(
              'twitter',
              `https://twitter.com/intent/tweet?text=${ENCODE(shareText)}&url=${ENCODE(url)}`
            )
          }
          className="inline-flex items-center gap-2 bg-gray-100 text-gray-800 hover:bg-gray-200 px-3 py-2 rounded-control text-sm font-semibold transition-colors pressable"
          aria-label="Share on X (Twitter)"
        >
          <FaXTwitter className="w-4 h-4" aria-hidden="true" />
          <span className="hidden xs:inline">X</span>
        </button>

        <button
          type="button"
          onClick={() =>
            handlePlatformShare(
              'facebook',
              `https://www.facebook.com/sharer/sharer.php?u=${ENCODE(url)}`
            )
          }
          className="inline-flex items-center gap-2 bg-[#1877F2]/10 text-[#0858B6] hover:bg-[#1877F2]/20 px-3 py-2 rounded-control text-sm font-semibold transition-colors pressable"
          aria-label="Share on Facebook"
        >
          <FaFacebookF className="w-4 h-4" aria-hidden="true" />
          <span className="hidden xs:inline">Facebook</span>
        </button>

        <button
          type="button"
          onClick={() =>
            handlePlatformShare(
              'email',
              `mailto:?subject=${ENCODE(shareText)}&body=${ENCODE(`Found this useful:\n\n${shareText}\n\n${url}`)}`
            )
          }
          className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 hover:bg-gray-200 px-3 py-2 rounded-control text-sm font-semibold transition-colors pressable"
          aria-label="Share by email"
        >
          <FaEnvelope className="w-4 h-4" aria-hidden="true" />
          <span className="hidden xs:inline">Email</span>
        </button>

        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 hover:bg-gray-200 px-3 py-2 rounded-control text-sm font-semibold transition-colors pressable"
          aria-label="Copy link"
        >
          <FaLink className="w-4 h-4" aria-hidden="true" />
          <span className="hidden xs:inline">
            {copyState === 'copied' ? 'Copied!' : copyState === 'error' ? 'Copy failed' : 'Copy link'}
          </span>
        </button>
      </div>
    </aside>
  )
}
